import { useMemo, useState, type ReactNode } from 'react';
import { Input } from '@sara/ui/components/input';
import { cn } from '@sara/ui/lib/utils';
import { ChevronRight, Search, X } from 'lucide-react';

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return typeof value === 'object' && value !== null;
}

/** String form of a primitive for searching/highlighting, or null for containers. */
function primitiveText(value: unknown): string | null {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return null;
}

/**
 * Does this key/value subtree contain a case-insensitive match for `q`?
 * `q` is assumed already lowercased; `searchKey` is null for array elements
 * (indices aren't searchable).
 */
function subtreeMatches(searchKey: string | null, value: unknown, q: string): boolean {
  if (!q) return true;
  if (searchKey !== null && searchKey.toLowerCase().includes(q)) return true;
  const text = primitiveText(value);
  if (text !== null) return text.toLowerCase().includes(q);
  if (Array.isArray(value)) return value.some((item) => subtreeMatches(null, item, q));
  return Object.entries(value as Record<string, unknown>).some(([k, v]) => subtreeMatches(k, v, q));
}

function collectContainerPaths(value: unknown, path: string, acc: string[]): void {
  if (Array.isArray(value)) {
    acc.push(path);
    value.forEach((item, i) => collectContainerPaths(item, `${path}.${i}`, acc));
  } else if (isContainer(value)) {
    acc.push(path);
    Object.entries(value).forEach(([k, v]) => collectContainerPaths(v, `${path}.${k}`, acc));
  }
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return text;
  const lower = text.toLowerCase();
  let from = lower.indexOf(query);
  if (from === -1) return text;

  const parts: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  while (from !== -1) {
    if (from > cursor) parts.push(text.slice(cursor, from));
    parts.push(
      <mark key={key++} className="rounded-sm bg-warning/30 text-foreground">
        {text.slice(from, from + query.length)}
      </mark>
    );
    cursor = from + query.length;
    from = lower.indexOf(query, cursor);
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function PrimitiveValue({ value, query }: { value: unknown; query: string }) {
  if (value === null || value === undefined) {
    return <span className="italic text-muted-foreground">null</span>;
  }
  if (typeof value === 'string') {
    // Multi-line strings (e.g. conversation_summary) carry real \n; preserve them
    // so the value reads as written instead of collapsing to one run-on line.
    return (
      <span className="whitespace-pre-wrap text-foreground/80">
        <span className="text-muted-foreground/40">&quot;</span>
        <Highlight text={value} query={query} />
        <span className="text-muted-foreground/40">&quot;</span>
      </span>
    );
  }
  if (typeof value === 'number') {
    return (
      <span className="text-primary tabular-nums">
        <Highlight text={String(value)} query={query} />
      </span>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <span className="text-warning">
        <Highlight text={value ? 'true' : 'false'} query={query} />
      </span>
    );
  }
  return <span>{String(value)}</span>;
}

interface NodeProps {
  /** Displayed label: object key or array index. */
  label: string;
  /** Key used for search matching, or null for array elements. */
  searchKey: string | null;
  value: unknown;
  path: string;
  query: string;
  collapsed: Set<string>;
  onToggle: (path: string) => void;
}

function Node({ label, searchKey, value, path, query, collapsed, onToggle }: NodeProps) {
  if (query && !subtreeMatches(searchKey, value, query)) return null;

  // Object keys read as deliberate labels — medium-weight warm terracotta,
  // distinct from the cool indigo of numbers. Array indices are dim ordinals.
  const labelEl =
    searchKey === null ? (
      <span className="tabular-nums text-muted-foreground/40">{label}</span>
    ) : (
      <span className="font-medium text-chart-1 dark:text-[oklch(0.74_0.13_40)]">
        <Highlight text={label} query={query} />
      </span>
    );

  if (!isContainer(value)) {
    return (
      <div className="flex items-start gap-1.5">
        <span className="w-4 shrink-0" aria-hidden />
        <span className="min-w-0 break-words">
          {labelEl}
          <span className="text-muted-foreground/50">: </span>
          <PrimitiveValue value={value} query={query} />
        </span>
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries: [string, unknown, string | null][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v, null])
    : Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, v, k]);
  const open = (query ? true : !collapsed.has(path)) && entries.length > 0;
  const [openTok, closeTok] = isArray ? ['[', ']'] : ['{', '}'];

  return (
    <div>
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          onClick={() => onToggle(path)}
          disabled={entries.length === 0}
          className="flex w-4 shrink-0 items-center justify-center pt-[3px] text-muted-foreground/60 transition-colors hover:text-foreground disabled:opacity-0"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={cn('h-3 w-3 transition-transform', open && 'rotate-90')}
            strokeWidth={2}
          />
        </button>
        <span className="min-w-0">
          {labelEl}
          <span className="text-muted-foreground/50">: </span>
          <span className="text-muted-foreground/50">{openTok}</span>
          {!open && (
            <button
              type="button"
              onClick={() => entries.length > 0 && onToggle(path)}
              className="mx-1 rounded bg-secondary/60 px-1.5 text-[11px] text-muted-foreground hover:bg-secondary"
            >
              {entries.length === 0 ? '' : `${entries.length} ${isArray ? 'items' : 'keys'}`}
            </button>
          )}
          {!open && <span className="text-muted-foreground/50">{closeTok}</span>}
        </span>
      </div>

      {open && (
        <>
          <div className="ml-[7px] border-l border-border/60 pl-3">
            {entries.map(([childLabel, childValue, childKey]) => (
              <Node
                key={childLabel}
                label={childLabel}
                searchKey={childKey}
                value={childValue}
                path={`${path}.${childLabel}`}
                query={query}
                collapsed={collapsed}
                onToggle={onToggle}
              />
            ))}
          </div>
          <div className="flex items-start gap-1.5">
            <span className="w-4 shrink-0" aria-hidden />
            <span className="text-muted-foreground/50">{closeTok}</span>
          </div>
        </>
      )}
    </div>
  );
}

/** Collapsible, searchable JSON tree for a result object. */
export function JsonViewer({ value }: { value: Record<string, unknown> }) {
  const [rawQuery, setRawQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const query = rawQuery.trim().toLowerCase();

  const entries = useMemo(() => Object.entries(value), [value]);
  const allPaths = useMemo(() => {
    const acc: string[] = [];
    entries.forEach(([k, v]) => collectContainerPaths(v, `$.${k}`, acc));
    return acc;
  }, [entries]);

  const toggle = (path: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const visible = query ? entries.filter(([k, v]) => subtreeMatches(k, v, query)) : entries;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.8}
          />
          <Input
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            placeholder="Search keys & values…"
            className="h-8 pl-8 pr-8 text-[13px]"
          />
          {rawQuery && (
            <button
              type="button"
              onClick={() => setRawQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(new Set())}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(new Set(allPaths))}
          className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          Collapse all
        </button>
      </div>

      <div className="overflow-x-auto font-mono text-[12.5px] leading-relaxed">
        {visible.length === 0 ? (
          <p className="py-2 text-[13px] font-sans italic text-muted-foreground">
            No keys or values match &ldquo;{rawQuery.trim()}&rdquo;.
          </p>
        ) : (
          visible.map(([k, v]) => (
            <Node
              key={k}
              label={k}
              searchKey={k}
              value={v}
              path={`$.${k}`}
              query={query}
              collapsed={collapsed}
              onToggle={toggle}
            />
          ))
        )}
      </div>
    </div>
  );
}
